import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Client } from '../../../models/client.model';
import { LoginService } from '../../../services/login.service';
import { UserService } from '../../../services/user.service';
import { CustomValidator } from '../../../utilities/custom-validator';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

declare var window: any;
declare var FB: any;

enum Tabs {
  logIn = 0,
  signUp = 1
}

enum ViewState {
  emailEntry = 'emailEntry',
  codeEntry = 'codeEntry',
  newClientInfo = 'newClientInfo'
}

@Component({
  selector: 'client-login',
  templateUrl: 'client-login.component.html',
  styleUrls: ['./client-login.component.scss']
})

export class ClientLoginComponent implements OnInit, OnDestroy {

  public isLoading: boolean;

  public submitted: boolean;

  public clientForm: FormGroup;

  public email: FormControl;

  public password: FormControl;

  public otpCode: FormControl;

  public firstName: FormControl;

  public lastName: FormControl;

  public clientPhone: FormControl;

  public allowSignUp: boolean;

  public allowFBLogin: boolean;

  public selectedTab: FormControl;

  public loginError: boolean;

  public tabIndex: Tabs;

  public viewState: ViewState;

  public authMode: string;

  // In 'both' mode the password field stays hidden until the user opts into
  // password login. (Always considered revealed in password-only mode.)
  public passwordRevealed: boolean;

  @ViewChild('passwordInput') public passwordInput: ElementRef;

  public codeEmail: string;

  public codeError: string;

  public remainingAttempts: number;

  public resendCooldown: number;

  public codeSubmitted: boolean;

  public newClientSubmitted: boolean;

  private resendInterval: any;

  constructor(private userService: UserService,
    private loginService: LoginService,
    private formBuilder: FormBuilder,
    private router: Router,
    public mdDialogRef: MatDialogRef<ClientLoginComponent>,
    @Inject(MAT_DIALOG_DATA) public modalData: any) {
    this.submitted = false;
    this.codeSubmitted = false;
    this.newClientSubmitted = false;
    this.isLoading = false;
    this.resendCooldown = 0;

    // form controls
    this.email = new FormControl('', Validators.compose([Validators.required]));
    this.password = new FormControl('');
    this.otpCode = new FormControl('', Validators.compose([
      Validators.required,
      Validators.minLength(6),
      Validators.maxLength(6),
      Validators.pattern('[0-9]*')
    ]));
    this.firstName = new FormControl('', Validators.required);
    this.lastName = new FormControl('', Validators.required);
    this.clientPhone = new FormControl('', Validators.compose([
      Validators.required,
      CustomValidator.validatePhone
    ]));

    this.clientForm = formBuilder.group({
      email: this.email,
      password: this.password
    });
    this.allowSignUp = true;
    this.allowFBLogin = true;
    this.selectedTab = new FormControl(0);
  }

  public ngOnInit() {

    if (window.FB) {
      window.FB.XFBML.parse();
    }

    this.allowSignUp = this.modalData ? this.modalData['allowSignUp'] : false;
    this.allowFBLogin = this.modalData ? this.modalData['allowFBLogin'] : false;

    const salon = this.userService.getSalon();
    this.authMode = salon ? salon.getClientAuthMode() : 'password';

    // Magic-code-only mode is passwordless, so the password-based sign-up tab
    // never applies. Treat sign-up as unavailable here so the magic-code
    // "unknown email" prompt follows the same rule as the (hidden) sign-up tab
    // and doesn't offer account creation.
    if (this.authMode === 'magic_code') {
      this.allowSignUp = false;
    }

    // Password-only salons show the field straight away; 'both' salons reveal
    // it only after the client chooses to log in with a password.
    this.passwordRevealed = this.authMode === 'password';

    this.viewState = ViewState.emailEntry;
    this.tabIndex = Tabs.logIn;

    if (!salon || this.userService.isLoggedIn()) {
      this.router.navigate(['/']);
    }
  }

  public ngOnDestroy() {
    this.clearResendTimer();
  }

  // --- Auth mode helpers ---

  public showForgotPassword(): boolean {
    return this.authMode !== 'magic_code'
      && this.viewState === ViewState.emailEntry
      && this.passwordRevealed;
  }

  public showSignupTab(): boolean {
    return this.allowSignUp && this.authMode !== 'magic_code';
  }

  // --- Password login (existing flow) ---

  // In 'both' mode the first tap on "Log in with password" just reveals the
  // password field (and focuses it); once revealed it logs in as normal.
  public onPasswordButton($event = null) {
    if (this.authMode === 'both' && !this.passwordRevealed) {
      if ($event) {
        $event.preventDefault();
      }
      this.passwordRevealed = true;
      setTimeout(() => {
        if (this.passwordInput) {
          this.passwordInput.nativeElement.focus();
        }
      });
      return;
    }
    this.onClientLogin($event);
  }

  public onClientLogin($event = null) {
    if ($event) {
      $event.preventDefault();
    }
    this.submitted = true;
    this.loginError = false;
    if (this.clientForm.valid === false) {
      return;
    }

    this.isLoading = true;
    this.loginService.loginUser(this.email.value, this.password.value)
      .subscribe(
        response => {
          this.isLoading = false;
          this.userService.login(response.user, response.token).then(result => {
            this.mdDialogRef.close(this.userService.getUser());
            this.loginError = false;
          });
        },
        error => {
          this.isLoading = false;
          this.loginError = true;
        }
      );
  }

  // --- Magic code flow ---

  public onSendCode($event = null) {
    if ($event) {
      $event.preventDefault();
    }
    this.submitted = true;
    this.codeError = null;
    this.loginError = false;

    if (!this.email.valid) {
      return;
    }

    this.isLoading = true;
    this.codeEmail = this.email.value;

    this.loginService.requestMagicCode(
      this.email.value,
      this.userService.getSalon().getId()
    ).subscribe(
      response => {
        this.isLoading = false;
        if (response.status === 'unknown_email') {
          // No account on file — prompt the user to create one rather than
          // dropping them straight into the sign-up form.
          this.codeError = 'unknown_email';
        } else {
          this.viewState = ViewState.codeEntry;
          this.startResendCooldown();
        }
      },
      error => {
        this.handleCodeRequestError(error);
      }
    );
  }

  // From the "unknown email" prompt — move the user into account creation.
  // codeEmail was already captured in onSendCode before the request.
  // Gated by the same allowSignUp rule as normal sign-up: if the salon has
  // sign-up turned off, the magic-code route can't create an account either.
  public goToCreateAccount() {
    if (!this.allowSignUp) {
      return;
    }
    this.codeError = null;
    this.viewState = ViewState.newClientInfo;
  }

  public onSubmitNewClientInfo($event = null) {
    if ($event) {
      $event.preventDefault();
    }
    this.newClientSubmitted = true;

    if (!this.email.valid || !this.firstName.valid || !this.lastName.valid || !this.clientPhone.valid) {
      return;
    }

    // Email is editable on this view, so use its current value.
    this.codeEmail = this.email.value;

    this.isLoading = true;
    this.loginService.requestMagicCode(
      this.codeEmail,
      this.userService.getSalon().getId(),
      {
        firstName: this.firstName.value,
        lastName: this.lastName.value,
        phone: this.clientPhone.value
      }
    ).subscribe(
      response => {
        this.isLoading = false;
        this.viewState = ViewState.codeEntry;
        this.startResendCooldown();
      },
      error => {
        this.handleCodeRequestError(error);
        if (this.codeError === 'rate_limited') {
          this.viewState = ViewState.codeEntry;
        }
      }
    );
  }

  public onVerifyCode($event = null) {
    if ($event) {
      $event.preventDefault();
    }
    this.codeSubmitted = true;
    this.codeError = null;

    if (!this.otpCode.valid) {
      return;
    }

    this.isLoading = true;
    this.loginService.verifyMagicCode(
      this.codeEmail,
      this.otpCode.value,
      this.userService.getSalon().getId()
    ).subscribe(
      response => {
        this.isLoading = false;
        this.userService.login(response.user, response.token).then(result => {
          this.mdDialogRef.close(this.userService.getUser());
        });
      },
      error => {
        this.isLoading = false;
        if (error && error.error === 'invalid_code') {
          this.codeError = 'invalid_code';
          this.remainingAttempts = error.remainingAttempts;
        } else if (error && error.error === 'code_expired') {
          this.codeError = 'code_expired';
        } else if (error && error.error === 'code_invalidated') {
          this.codeError = 'code_invalidated';
        } else {
          this.loginError = true;
        }
      }
    );
  }

  public onResendCode() {
    if (this.resendCooldown > 0) {
      return;
    }
    this.codeError = null;
    this.otpCode.setValue('');
    this.codeSubmitted = false;
    this.onSendCodeDirect();
  }

  private onSendCodeDirect() {
    this.isLoading = true;
    this.loginService.requestMagicCode(
      this.codeEmail,
      this.userService.getSalon().getId()
    ).subscribe(
      response => {
        this.isLoading = false;
        this.startResendCooldown();
      },
      error => {
        this.handleCodeRequestError(error);
      }
    );
  }

  // --- Navigation between views ---

  public onSwitchToCode() {
    this.submitted = true;
    this.loginError = false;
    if (!this.email.valid) {
      return;
    }
    this.onSendCode();
  }

  public onBackToEmail() {
    this.clearResendTimer();
    this.viewState = ViewState.emailEntry;
    this.codeError = null;
    this.codeSubmitted = false;
    this.otpCode.setValue('');
    this.submitted = false;
  }

  // --- Error handling helpers ---

  private handleCodeRequestError(error: any) {
    this.isLoading = false;
    if (error && error.status === 429) {
      const retryAfter = error.retryAfter || 600;
      this.codeError = 'rate_limited';
      this.remainingAttempts = Math.ceil(retryAfter / 60);
    } else {
      this.loginError = true;
    }
  }

  // --- Resend cooldown timer ---

  private startResendCooldown() {
    this.resendCooldown = 30;
    this.clearResendTimer();
    this.resendInterval = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
  }

  // --- Facebook login (existing) ---

  public onFacebookLogin() {
    this.isLoading = true;
    this.loginError = false;

    FB.login((response: any) => {
      if (response.status === 'connected') {
        FB.api('/me', { fields: 'first_name, last_name, email' }, (userResponse) => {
          const id: string = userResponse['id'];
          const email: string = userResponse['email'];
          const firstName: string = userResponse['first_name'];
          const lastName: string = userResponse['last_name'];

          this.loginService.facebookLogin(id, firstName, lastName, email)
            .subscribe((response) => {
              this.isLoading = false;
              this.userService.login(response.user, response.token);
              this.mdDialogRef.close(this.userService.getUser());
              this.loginError = false;
            },
              (err) => {
                this.isLoading = false;
                this.loginError = true;
              });
        });
      } else {
        this.isLoading = false;
      }
    }, {
      scope: 'public_profile, email',
      return_scopes: true
    });
  }

  // --- Signup + tab navigation (existing) ---

  public onSignUp(client: Client) {
    this.email.setValue(client.getEmail());
    this.password.setValue(client.getPassword());
    this.onClientLogin();
  }

  public forgotPassword() {
    this.router.navigate(['/passwordreset/request']);
    this.mdDialogRef.close();
  }

  public staffLogin() {
    window.location.replace("https://" + this.userService.getSalon().getSubDomain() + ".salonmonster.com/6/salon");
  }

  public showLogin() {
    this.tabIndex = Tabs.logIn;
  }

  public showSignup() {
    this.tabIndex = Tabs.signUp;
  }

}
